/**
 * Persistent outbox for failed Turso sync writes.
 *
 * When a write to Turso fails (network error, timeout, etc.), the query
 * and params are stored in a local-only SQLite table (sync_pending_writes).
 * Pending writes are replayed in FIFO order when connectivity is restored.
 *
 * This module has NO imports from other project files to avoid circular
 * dependencies. Queue processing accepts sync functions via dependency
 * injection so tursoSync.ts and index.ts can both use it without cycles.
 */

const MAX_RETRY_ATTEMPTS = 10;

let _sqlDb: any = null;

export interface OutboxEntry {
  id: number;
  query: string;
  params: string; // JSON-serialized
  is_batch: number; // 0 = single, 1 = batch
  created_at: string;
  attempts: number;
  last_attempt_at: string | null;
  last_error: string | null;
}

/**
 * Initialize the outbox with a sql.js database reference.
 * Creates the local-only outbox table if it doesn't exist.
 * This table is NOT synced to Turso (that would be contradictory).
 */
export function initOutbox(sqlDb: any): void {
  _sqlDb = sqlDb;
  sqlDb.run(`
    CREATE TABLE IF NOT EXISTS sync_pending_writes (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      query TEXT NOT NULL,
      params TEXT NOT NULL,
      is_batch INTEGER DEFAULT 0,
      created_at TEXT NOT NULL,
      attempts INTEGER DEFAULT 0,
      last_attempt_at TEXT,
      last_error TEXT
    )
  `);
}

/**
 * Enqueue a single failed write for retry.
 */
export function enqueueWrite(query: string, params: any[]): void {
  if (!_sqlDb) return;
  _sqlDb.run(
    'INSERT INTO sync_pending_writes (query, params, is_batch, created_at) VALUES (?, ?, 0, ?)',
    [query, JSON.stringify(params), new Date().toISOString()]
  );
  console.info('[SyncOutbox] Write encolado para reintento:', query.substring(0, 80));
}

/**
 * Enqueue a batch of failed writes for retry.
 */
export function enqueueBatchWrite(statements: Array<{ sql: string; args: any[] }>): void {
  if (!_sqlDb) return;
  _sqlDb.run(
    'INSERT INTO sync_pending_writes (query, params, is_batch, created_at) VALUES (?, ?, 1, ?)',
    ['__BATCH__', JSON.stringify(statements), new Date().toISOString()]
  );
  console.info(`[SyncOutbox] Batch de ${statements.length} statements encolado para reintento`);
}

/**
 * Get all pending writes in FIFO order (by AUTOINCREMENT id).
 */
export function getPendingWrites(): OutboxEntry[] {
  if (!_sqlDb) return [];
  const stmt = _sqlDb.prepare('SELECT * FROM sync_pending_writes ORDER BY id ASC');
  const results: OutboxEntry[] = [];
  while (stmt.step()) {
    results.push(stmt.getAsObject() as unknown as OutboxEntry);
  }
  stmt.free();
  return results;
}

/**
 * Remove a successfully processed write from the outbox.
 */
export function dequeueWrite(id: number): void {
  if (!_sqlDb) return;
  _sqlDb.run('DELETE FROM sync_pending_writes WHERE id = ?', [id]);
}

/**
 * Increment attempts and record the error for a failed write.
 */
export function markFailed(id: number, error: string): void {
  if (!_sqlDb) return;
  _sqlDb.run(
    'UPDATE sync_pending_writes SET attempts = attempts + 1, last_attempt_at = ?, last_error = ? WHERE id = ?',
    [new Date().toISOString(), error, id]
  );
}

/**
 * Get the count of pending writes in the outbox.
 */
export function getOutboxCount(): number {
  if (!_sqlDb) return 0;
  const stmt = _sqlDb.prepare('SELECT COUNT(*) as cnt FROM sync_pending_writes');
  stmt.step();
  const result = stmt.getAsObject();
  stmt.free();
  return Number(result.cnt) || 0;
}

/**
 * Returns the configured max retry attempts.
 */
export function getMaxRetryAttempts(): number {
  return MAX_RETRY_ATTEMPTS;
}

/**
 * Process the outbox queue in strict FIFO order.
 *
 * Sync functions are injected to avoid circular dependencies with tursoSync.ts.
 * Processing stops on the first retryable failure to preserve write ordering
 * (a later write might depend on an earlier one affecting the same row).
 *
 * Return value reports how many entries were processed and how many remain.
 *
 * Behavior per entry:
 * - syncFn returns true  -> dequeue (success)
 * - syncFn returns false + status 'synced' -> dequeue (FK error, non-retryable)
 * - syncFn returns false + other status   -> markFailed + stop processing
 * - syncFn throws        -> markFailed + stop processing
 * - attempts >= MAX_RETRY_ATTEMPTS        -> skip (don't retry forever)
 */
export async function processOutboxQueue(
  syncFn: (query: string, params: any[]) => Promise<boolean>,
  batchSyncFn: (stmts: Array<{ sql: string; args: any[] }>) => Promise<boolean>,
  getStatusFn: () => string
): Promise<{ processed: number; remaining: number }> {
  const pending = getPendingWrites();
  if (pending.length === 0) {
    return { processed: 0, remaining: 0 };
  }

  console.info(`[SyncOutbox] Procesando ${pending.length} writes pendientes...`);
  let processed = 0;

  for (const entry of pending) {
    if (entry.attempts >= MAX_RETRY_ATTEMPTS) {
      console.warn(`[SyncOutbox] Write id=${entry.id} excedio el limite de ${MAX_RETRY_ATTEMPTS} reintentos, omitiendo.`);
      continue;
    }

    let success: boolean;
    try {
      if (entry.is_batch) {
        const statements: Array<{ sql: string; args: any[] }> = JSON.parse(entry.params);
        success = await batchSyncFn(statements);
      } else {
        const params: any[] = JSON.parse(entry.params);
        success = await syncFn(entry.query, params);
      }
    } catch (err) {
      markFailed(entry.id, String(err));
      console.warn(`[SyncOutbox] Error inesperado procesando write id=${entry.id}:`, err);
      break;
    }

    if (success) {
      dequeueWrite(entry.id);
      processed++;
    } else {
      const status = getStatusFn();
      if (status === 'synced') {
        // Non-retryable: FK constraint or similar server-side rejection.
        // The write was received by Turso but rejected. Retrying won't help.
        dequeueWrite(entry.id);
        processed++;
        console.info(`[SyncOutbox] Write id=${entry.id} descartado (no reintentable, FK o similar).`);
      } else {
        // Retryable: network error, timeout, server unreachable.
        markFailed(entry.id, `Sync returned false, status: ${status}`);
        console.warn(`[SyncOutbox] Write id=${entry.id} fallo (status: ${status}), deteniendo procesamiento.`);
        break;
      }
    }
  }

  const remaining = getOutboxCount();
  if (remaining === 0) {
    console.info('[SyncOutbox] Cola vaciada exitosamente.');
  }

  return { processed, remaining };
}

/** For testing: reset module state. */
export function _resetOutboxForTesting(): void {
  _sqlDb = null;
}
